import { useState, useEffect, useRef } from "react"
import { PlaylistQuery, PlaylistType, getPlaylists } from "../playlist"
import axios from "axios"

export interface IFetchPlaylist {
  playlistsData: PlaylistType[]
  loading: boolean
  error: { status: number } | null
  refresh: (resetIfNull?: boolean) => Promise<void>
  nResults: number
}

export const usePlaylistAPI = (
  query: PlaylistQuery,
  abortOnChange = true
): IFetchPlaylist => {
  const [playlistsData, setPlaylists] = useState<PlaylistType[]>([])
  const [nResults, setNResults] = useState(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<{ status: number } | null>(null)
  const lastQueryId = useRef(0)

  const fetchPlaylists = async (
    queryParam: PlaylistQuery,
    resetIfNull = false
  ) => {
    lastQueryId.current += 1
    const queryId = lastQueryId.current
    setLoading(true)
    try {
      const playlistData = await getPlaylists(queryParam)
      if (abortOnChange && queryId !== lastQueryId.current) return
      setError(null)
      setPlaylists(playlistData.playlists)
      setNResults(playlistData.nResults)
    } catch (err: unknown) {
      if (abortOnChange && queryId !== lastQueryId.current) return
      if (axios.isAxiosError(err)) {
        setError({ status: err.status || 500 })
      } else {
        setError({ status: 500 })
      }
      if (resetIfNull) setPlaylists([])
    } finally {
      if (!(abortOnChange && queryId !== lastQueryId.current)) setLoading(false)
    }
  }

  const refresh = async (resetIfNull = false) => {
    await fetchPlaylists(query, resetIfNull)
  }

  useEffect(() => {
    fetchPlaylists(query)
  }, [query.code])
  return { playlistsData, loading, error, refresh, nResults }
}
